import _ from 'lodash';
import { skip } from 'rxjs/operators';
import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').Account} Account
 * @typedef {import('./state').AccountConfiguration} AccountConfiguration
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} LoadCurrentUserActionOpts
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * - returns the current user if set; else
 * - checks the cache for a valid token and signs in with it
 * @callback LoadCurrentUserAction
 * @param {ActionContext} ctx
 * @param {LoadCurrentUserActionOpts} opts
 * @returns {Promise<Account>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {LoadCurrentUserAction}
 */
export const loadCurrentUser = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  // check current state
  if (ctx.state.currentUser && !opts.$forceFetch) return ctx.state.currentUser;

  // fetch from sdk
  const currentUser = await sdk.auth().currentUser();
  ctx.commit('setCurrentUser', currentUser);

  // handle no current user
  if (!currentUser) return currentUser;

  // watch changes
  const currentUserSub = sdk.auth().currentUser$
    .pipe(skip(1))
    .subscribe(cu => {
      if (!cu || cu.uid !== currentUser.uid) return;
      ctx.commit('setCurrentUser', cu);
    });
  ctx.commit('setCurrentUserSub', currentUserSub);

  // TODO: change this to inhoouse web push implementation
  // update device tokens
  // const deviceTokensSub = sdk.notifications()
  //   .getDeviceToken$()
  //   .subscribe(token => {
  //     const deviceTokens = ctx.state.currentUser.deviceTokens || [];
  //     if (~deviceTokens.indexOf(token)) return;
  //     sdk.accounts().update(currentUser.uid, {
  //       $addToSet: { deviceTokens: token }
  //     });
  //   });
  // ctx.commit('setDeviceTokensSub', deviceTokensSub);

  // return current user
  return currentUser;
};

/**
 * @typedef {Object} SigninActionOpts
 * @property {'local'|'jwt'} strategy
 * @property {boolean} [setCurrentUser=true] - set the signed in user as the current user
 * @property {string} [email]
 * @property {string} [password]
 * @property {string} [totpToken]
 * @property {string} [accessToken] - for jwt strat
 *
 * @callback SigninAction
 * @param {ActionContext} ctx
 * @param {SigninActionOpts} opts
 * @returns {Promise<Account>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {SigninAction}
 */
export const signin = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  // opts
  const { strategy, setCurrentUser = true, ...payload } = opts;

  // reset store
  if (setCurrentUser) ctx.commit('setCurrentUser', null);

  // signin
  const user = await sdk.auth().signin(strategy, payload, setCurrentUser);

  // return signin result (signin credentials)
  if (!setCurrentUser) return user;

  // load current user to store
  return ctx.dispatch('loadCurrentUser');
};

/**
 * @callback SignoutAction
 * @param {ActionContext} ctx
 * @param {Object} opts
 * @returns {Promise<void>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {SignoutAction}
 */
export const signout = (store, sdk) => async (ctx, opts) => {
  // signout
  await sdk.auth().signout();
  ctx.commit('setCurrentUser', null);
};

/**
 * fetches the current active access token
 * that can be used for sso redirections
 * @callback GetAccessTokenAction
 * @param {ActionContext} ctx
 * @param {Object} opts
 * @returns {Promise<string>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetAccessTokenAction}
 */
export const getAccessToken = (store, sdk) => async (ctx, opts) => {
  // check current user
  if (!ctx.state.currentUser) throw new Error(`${nms}: No logged in user`);

  // execute
  return sdk.auth().getAccessToken();
};

/**
 * @typedef {Object} GetAccountConfigActionOpts
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * fetch account-specific configurations
 * @callback GetAccountConfigAction
 * @param {ActionContext} ctx
 * @param {GetAccountConfigActionOpts} opts
 * @returns {Promise<AccountConfiguration>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetAccountConfigAction}
 */
export const getCurrentUserConfig = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  // check current user
  if (!ctx.state.currentUser) throw new Error(`${nms}: No logged in user`);

  // check cache
  if (ctx.state.currentUserConfig && !opts.$forceFetch) return ctx.state.currentUserConfig;

  // fetch
  const config = await sdk.account.configurations().get(ctx.state.currentUser.uid);
  ctx.commit('setCurrentUserConfig', config);

  return config;
};

/**
 * @typedef {Object} UpdateAccountConfigActionOpts
 * @property {Object.<string,any>} emr update payload
 *
 * update account-specific configurations
 * @callback UpdateAccountConfigAction
 * @param {ActionContext} ctx
 * @param {UpdateAccountConfigActionOpts} opts
 * @returns {Promise<AccountConfiguration>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {UpdateAccountConfigAction}
 */
export const updateCurrentUserConfig = (store, sdk) => async (ctx, opts) => {
  if (!ctx.state.currentUser) throw new Error(`${nms}: No logged in user`);
  opts = Object.assign({}, opts);

  // execute
  const data = opts;
  if (!data) throw new Error(`${nms}: Update payload required`);
  if (_.isEmpty(data)) throw new Error(`${nms}: Empty update payload`);
  const config = await sdk.account.configurations().update(ctx.state.currentUser.uid, data);
  ctx.commit('setCurrentUserConfig', config);

  return config;
};

/**
 * @typedef {Object} ApplyActionCodeActionOpts
 * @property {string} code
 * @property {any} payload
 *
 * @callback ApplyActionCodeAction
 * @param {ActionContext} ctx
 * @param {ApplyActionCodeActionOpts} opts
 * @returns {Promise<void>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {ApplyActionCodeAction}
 */
export const applyActionCode = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  const { code, payload } = opts;
  if (!code) throw new Error(`${nms}: Code required`);

  await sdk.auth().applyActionCode(code, payload);
};
